import { computed, ref, watch } from "vue";
import { useTranslation } from "@/language/translation";
import { useStore } from "vuex";
import { useTranslationFunctionCode } from "@/components/menu/SidebarHelper";
import isModifierSet from "./userModifier";
import { isModuleActive } from "./moduleConfig";
import userMenu from "@/config/userMenu";
import { filterUserMenu } from "./userMenuFilter";
import {
  SQDCEventActionTypeInternalType,
  SQDCEventActionTypeType,
  useSQDCEventActionTypesQuery,
} from "@/graphql";
import type { UserMenuEntry } from "@/config/userMenu";
import { ModifierType } from "./userModifierEnum";
import { getLanguageValue } from "@/modules/sqdc/helper/SQDCEnumValueHelper";
import { useAuthorization } from "./useAuthorization";
import { FMFoldersForTreeDocument, type FMFoldersForTreeQuery } from "@/graphql";
import { apolloClient } from "@/graphql/apollo";
import { canManageFolder } from "@/modules/filemanagement/helper/FMFolderManagementAccess";

type MenuChildEntryType = {
  href?: string;
  icon?: string;
  title: string;
  menuKey?: string;
  badge?: { text: string; class?: string };
  child?: MenuChildEntryType[];
};

export type MenuEntryType = MenuChildEntryType & {
  callback?: (event: Event, item: MenuEntryType) => void;
};

export function useRawUserMenu() {
  const store = useStore();
  const { t, locale } = useTranslation();
  const lisa3Enabled = computed(() => isModifierSet(ModifierType.LISA3));
  const { result: eventActionTypesResult } = useSQDCEventActionTypesQuery(
    { includeDrafts: false },
    computed(() => ({ fetchPolicy: "cache-and-network", enabled: lisa3Enabled.value })),
  );
  const { hasRight, hasRightOnAnySite, ready, snapshot } = useAuthorization();
  const hasManageableFolder = ref(false);
  const canPotentiallyManageFolder = computed(() =>
    [
      "sqdc.write",
      "sqdc.admin",
      "audit.admin",
      "news.admin",
      "bde.admin",
      "systemSettings.admin",
    ].some(hasRightOnAnySite),
  );

  let requestId = 0;
  watch(
    [ready, () => snapshot.value?.revision, canPotentiallyManageFolder],
    async ([isReady, , hasPotentialRight]) => {
      const currentRequestId = ++requestId;
      hasManageableFolder.value = false;
      if (!isReady) return;

      try {
        const { data: root } = await apolloClient.query<FMFoldersForTreeQuery>({
          query: FMFoldersForTreeDocument,
          variables: { directChildrenOfFolderIds: 1 },
          fetchPolicy: "network-only",
        });
        if (currentRequestId !== requestId) return;

        const isSystemAdmin = hasRight("systemSettings.admin");
        if (
          [...root.FMFolders, ...root.FMFolderFavorites].some((folder) =>
            canManageFolder(folder, isSystemAdmin),
          )
        ) {
          hasManageableFolder.value = true;
          return;
        }

        if (!hasPotentialRight) return;

        const systemFolderId = root.FMSystemFolders[0]?.id;
        if (systemFolderId == null) return;

        const { data: system } = await apolloClient.query<FMFoldersForTreeQuery>({
          query: FMFoldersForTreeDocument,
          variables: { directChildrenOfFolderIds: systemFolderId },
          fetchPolicy: "network-only",
        });
        if (currentRequestId !== requestId) return;
        hasManageableFolder.value = system.FMFolders.some((folder) =>
          canManageFolder(folder, isSystemAdmin),
        );
      } catch {
        if (currentRequestId === requestId) hasManageableFolder.value = false;
      }
    },
    { immediate: true },
  );

  return computed(() => {
    const anonym = store.state.login.user?.anonym === true;
    const filtered = filterUserMenu(
      userMenu.value,
      hasRightOnAnySite,
      anonym,
      isModifierSet,
      isModuleActive,
    );
    const entries = structuredClone(filtered).filter(
      (entry) => entry.title !== "documents" || hasManageableFolder.value,
    );
    if (!lisa3Enabled.value) return entries;

    const templates = eventActionTypesResult.value?.SQDCEventActionTypes ?? [];
    const knowledgeTitle = t(
      "sidebar.problemSolving.children.PSC.children.knowledgeOverview.title",
    );
    const locations: Partial<
      Record<SQDCEventActionTypeInternalType, { main: string; group: string }>
    > = {
      [SQDCEventActionTypeInternalType.PSC]: {
        main: "problemSolving",
        group: "PSC",
      },
      [SQDCEventActionTypeInternalType.IH]: { main: "ih", group: "maintenance" },
      [SQDCEventActionTypeInternalType.Quality]: { main: "quality", group: "charts" },
      [SQDCEventActionTypeInternalType.Audit]: { main: "audit", group: "audit" },
      [SQDCEventActionTypeInternalType.Idea]: { main: "hlp", group: "hlpSettings" },
    };

    for (const [internalType, location] of Object.entries(locations) as [
      SQDCEventActionTypeInternalType,
      NonNullable<(typeof locations)[SQDCEventActionTypeInternalType]>,
    ][]) {
      const group = entries
        .find(({ title }) => title === location.main)
        ?.child?.find(({ title }) => title === location.group);
      if (group == null) continue;
      const lists = new Map<string, string>();
      for (const template of templates) {
        if (
          template.type === SQDCEventActionTypeType.EVENT &&
          template.internalType === internalType &&
          template.listName != null &&
          template.listName.trim() !== ""
        ) {
          lists.set(
            template.listName,
            getLanguageValue(template.ListName, locale.value) || template.listName,
          );
        }
      }
      const listEntries: UserMenuEntry[] = [...lists].map(([listName, label]) => ({
        title: `eventTypeList-${internalType}-${listName}`,
        literalTitle: label,
        href: `/sqdc/type-list/${internalType}?list=${encodeURIComponent(listName)}`,
        onlyOnModifier: ModifierType.LISA3,
      }));
      if (internalType !== SQDCEventActionTypeInternalType.Audit) {
        listEntries.push({
          title: `eventTypeKnowledge-${internalType}`,
          literalTitle: knowledgeTitle,
          href: `/sqdc/type-list/${internalType}?archive=true`,
          onlyOnModifier: ModifierType.LISA3,
        });
      }
      group.child = [...listEntries, ...(group.child ?? [])];
    }
    return entries;
  });
}

export function useUserMenu() {
  const translationFunctionCode = useTranslationFunctionCode();
  const { t } = useTranslation();
  const rawMenu = useRawUserMenu();

  const menu = computed((): MenuEntryType[] => {
    return rawMenu.value.map((main) => {
      return {
        ...main,
        menuKey: main.title,
        title:
          main.literalTitle ??
          t(
            main.translationFunctionCode != null
              ? (translationFunctionCode(main.translationFunctionCode) ??
                  `sidebar.${main.title}.title`)
              : `sidebar.${main.title}.title`,
          ),
        badge: main.badge,
        icon: main.icon ?? "",
        href: main.href ?? `/redirect/${main.title}`,
        child: main.child?.map((level1) => {
          return {
            ...level1,
            menuKey: level1.title,
            href: level1.href ?? `/redirect/${level1.title}`,
            title:
              level1.literalTitle ??
              t(
                level1.translationFunctionCode != null
                  ? (translationFunctionCode(level1.translationFunctionCode) ??
                      `sidebar.${main.title}.children.${level1.title}.title`)
                  : `sidebar.${main.title}.children.${level1.title}.title`,
              ),
            child: level1.child?.map((level2) => {
              return {
                ...level2,
                menuKey: level2.title,
                href: level2.href ?? `/redirect/${level2.title}`,
                class: level2.onlyOnModifier != null ? "text-warn" : undefined,
                title:
                  (level2.onlyOnModifier != null ? "NEW_" : "") +
                  (level2.literalTitle ??
                    t(
                      level2.translationFunctionCode != null
                        ? (translationFunctionCode(level2.translationFunctionCode) ??
                            `sidebar.${main.title}.children.${level1.title}.children.${level2.title}.title`)
                        : `sidebar.${main.title}.children.${level1.title}.children.${level2.title}.title`,
                    )),
              };
            }),
          };
        }),
      };
    });
  });

  return {
    menu,
  };
}
